import React, { useRef, useEffect } from "react";
import * as d3 from "d3";
import { editSmall as hoverIcon } from "../../assets/images/icons";

const ForceGraphDetail = ({
  settings: propSettings = {},
  jsonData = [],
  itemClickedObject,
  categoryColors = {},
  onClicked,
  onChange,
}) => {
  const svgRef = useRef(null);
  const isEditable = true;

  const localSettings = {
    blackDotRadius: 6,
    curveOffset: 40,
    curveJitter: 50,
    exitDuration: 500,
    introDuration: 1000,
    introDelayStep: 50,
    linkDistanceMargin: 50,
    smallCircleFont: "12px sans-serif",
    smallCircleRadius: 14,
    smallCirclePadding: 10,
    pushFactor: 0.5,
    font: "14px sans-serif",
    border: 2,
    collisionPadding: 2,
    nodeMargin: 2,
    simulationTicks: 0,
    height: propSettings.height || 400,
    ...propSettings,
  };

  const onChangeFunc = ({type, editSource, editIndex, d}) => {
    const getClickedDataId = jsonData.filter(ld => ld.source === editSource).find(ld => ld.targetStr === d.target.title).id
    onChange({
      type: type,
      editSource: editSource,
      editIndex: getClickedDataId
    });
  };

  const generateGraph = () => {
    const svg = d3.select(svgRef.current);
    svg.selectAll("*").remove();

    const width =
      localSettings.width || svg.node().getBoundingClientRect().width;
    const height = localSettings.height;

    // Background rect (unchanged)
    svg
      .append("rect")
      .attr("class", "background-rect")
      .attr("x", 0)
      .attr("y", 0)
      .attr("width", width)
      .attr("height", height)
      .attr("fill", "transparent")
      .lower()
      .on("click", (event) => {
        const obj = { ...itemClickedObject.details[0] };
        handleNodeClick(obj);
      });

    const nodes = itemClickedObject.details;
    const links = itemClickedObject.details
      .map((d, i) => ({ source: 0, target: i }))
      .filter((f) => f.source !== f.target);

    const simulation = d3
      .forceSimulation(nodes)
      .force(
        "link",
        d3
          .forceLink(links)
          .id((d) => d.id)
          .distance(() => 110 + localSettings.linkDistanceMargin)
      )
      .force(
        "charge",
        d3.forceManyBody().strength(localSettings.chargeStrength || -200)
      )
      .force("center", d3.forceCenter(width / 2, height / 2))
      .force(
        "collision",
        d3
          .forceCollide()
          .radius((d) =>
            (d.size || 0) +
            localSettings.collisionPadding +
            localSettings.nodeMargin
          )
      )
      .force("x", d3.forceX(width / 2).strength(-0.4))
      .force("y", d3.forceY(height / 2).strength(-0.1));

    if (localSettings.simulationTicks > 0) {
      for (let i = 0; i < localSettings.simulationTicks; i++) {
        simulation.tick();
      }
    }

    // LINKS
    const link = svg
      .append("g")
      .attr("class", "links")
      .selectAll("path")
      .data(links)
      .join("path")
      .attr("fill", "none")
      .attr("stroke", "#333")
      .attr("stroke-opacity", 0.5)
      .attr("stroke-width", 1.5)
      .attr("opacity", 0);

    // BIG NODES
    const node = svg
      .append("g")
      .attr("class", "nodes")
      .selectAll("circle")
      .data(nodes)
      .join("circle")
      // Add classes: parent (for node‑0) or child (for others)
      .classed("parent", (d) => d.id === 0)
      .classed("child", (d) => d.id !== 0)
      .attr("r", 0)
      .attr("fill", "white")
      .attr("stroke", (d) => {
        const key = d.category ? d.category.replaceAll(" ", "") : "";
        return categoryColors[key] || "black";
      })
      .attr("stroke-width", localSettings.border)
      .attr("id", (d) => "node-" + d.id)
      .on("click", (event, d) => {
        event.stopPropagation();
        handleNodeClick(d);
      });

    // BIG NODE LABELS
    const labels = svg
      .append("g")
      .attr("class", "node-labels")
      .selectAll("text")
      .data(nodes)
      .join("text")
      .attr("class", "label")
      .style("font", localSettings.font)
      .attr("pointer-events", "none")
      .attr("opacity", 0)
      .each(function (d) {
        const parts = d.title.split(" ");
        const mid = Math.ceil(parts.length / 2);
        const line1 = parts.slice(0, mid).join(" ");
        const line2 = parts.slice(mid).join(" ");
        const thisText = d3.select(this);
        thisText
          .append("tspan")
          .text(line1)
          .style("font", localSettings.font)
          .attr("x", 0)
          .attr("dy", "-0.3em");
        thisText
          .append("tspan")
          .text(line2)
          .style("font", localSettings.font)
          .attr("x", 0)
          .attr("dy", "1.2em");
      });

    // SMALL CIRCLES + SMALL TEXT (unchanged)
    const smallGroup = svg.append("g").attr("class", "smallCirclesGroup");
    const smallCircles = smallGroup
      .selectAll("circle.smallCircle")
      .data(links)
      .join("circle")
      .attr("class", "smallCircle")
      .attr("r", 0)
      .attr("fill", "#fff")
      .attr("stroke", "#000")
      .attr("stroke-width", 1)
      .attr("pointer-events", "all");

    const smallTexts = smallGroup
      .selectAll("text.smallCircleText")
      .data(links)
      .join("text")
      .attr("class", "smallCircleText")
      .attr("text-anchor", "middle")
      .attr("dominant-baseline", "central")
      .style("font", localSettings.smallCircleFont)
      .text((d) => (d.target.value != null ? d.target.value + "%" : ""))
      .attr("pointer-events", "none")
      .attr("opacity", 0);

    // BLACK DOT (for id = 0) (unchanged)
    const blackDots = svg
      .append("g")
      .attr("class", "blackDotsGroup")
      .selectAll("circle.blackDot")
      .data(nodes.filter((d) => d.id === 0))
      .join("circle")
      .attr("class", "blackDot")
      .attr("r", 0)
      .attr("fill", "black")
      .attr("pointer-events", "none");

    // NEW: Gray hover circle
    // We'll create a “hover circle” group; default hidden.
    const hoverGroup = svg
      .append("g")
      .attr("class", "hoverCircleGroup")
      .style("pointer-events", "none"); // so it doesn't block mouse
    const hoverCircle = hoverGroup
      .append("circle")
      .attr("class", "hover-dot")
      .attr("r", localSettings.blackDotRadius) // same radius as blackDot (or change if you like)
      .attr("fill", "#888888")
      .attr("opacity", 0);

    // Intro transitions (unchanged)
    link.transition().duration(localSettings.introDuration).attr("opacity", 1);

    node
      .transition()
      .delay((d, i) => i * localSettings.introDelayStep)
      .duration(localSettings.introDuration)
      .attr("r", (d) => d.size || 0);

    blackDots
      .transition()
      .delay(nodes.findIndex((d) => d.id === 0) * localSettings.introDelayStep)
      .duration(localSettings.introDuration)
      .attr("r", localSettings.blackDotRadius);

    labels
      .transition()
      .delay((d, i) => i * localSettings.introDelayStep + localSettings.introDuration / 3)
      .duration(localSettings.introDuration)
      .attr("opacity", 1);

    smallCircles
      .transition()
      .delay((d, i) => i * localSettings.introDelayStep + localSettings.introDuration / 2)
      .duration(localSettings.introDuration)
      .attr("r", localSettings.smallCircleRadius);

    smallTexts
      .transition()
      .delay((d, i) => i * localSettings.introDelayStep + localSettings.introDuration / 2)
      .duration(localSettings.introDuration)
      .attr("opacity", 1);

    // Editable hover / click behavior (keep your existing code)
    let editIconGroup = null;
    if (isEditable) {
      editIconGroup = svg.append("g").attr("class", "small-edit-icons");

      smallCircles
        .on("mouseover", function (event, d) {
          d3.select(this).classed("hovered-circle", true);

          smallTexts.filter((dt) => dt === d).classed("hovered-text", true);
          link.filter((lt) => lt === d).classed("hovered-link", true);

          editIconGroup.selectAll(".edit-icon").remove();
          const iconSize = 16;
          const offsetX = localSettings.smallCircleRadius + 4;

          const icon = editIconGroup
            .append("image")
            .attr("class", "edit-icon")
            .attr("href", hoverIcon)
            .attr("width", iconSize)
            .attr("height", iconSize)
            .attr("pointer-events", "none");

          if (d._sx != null && d._sy != null) {
            icon.attr("x", d._sx + offsetX).attr("y", d._sy - iconSize / 2);
          } else {
            const [cx, cy] = d3.pointer(event, svg.node());
            icon.attr("x", cx + offsetX).attr("y", cy - iconSize / 2);
          }
        })
        .on("mouseout", function (event, d) {
          d3.select(this).classed("hovered-circle", false);

          smallTexts.filter((dt) => dt === d).classed("hovered-text", false);
          link.filter((lt) => lt === d).classed("hovered-link", false);

          if (editIconGroup) {
            editIconGroup.selectAll(".edit-icon").remove();
          }
        })
        .on("click", function (event, d) {
          event.stopPropagation();
          onChangeFunc({
            type: "edit",
            editSource: d.source.title,
            editIndex: itemClickedObject.index,
            d: d
          })
        });

      smallTexts
        .on("mouseover", function (event, d) {
          d3.select(this).classed("hovered-text", true);

          smallCircles.filter((dc) => dc === d).classed("hovered-circle", true);
          link.filter((lt) => lt === d).classed("hovered-link", true);

          editIconGroup.selectAll(".edit-icon").remove();
          const iconSize = 16;
          const offsetX = localSettings.smallCircleRadius + 4;

          const icon = editIconGroup
            .append("image")
            .attr("class", "edit-icon")
            .attr("href", hoverIcon)
            .attr("width", iconSize)
            .attr("height", iconSize)
            .attr("pointer-events", "none");

          if (d._sx != null && d._sy != null) {
            icon.attr("x", d._sx + offsetX).attr("y", d._sy - iconSize / 2);
          } else {
            const [cx, cy] = d3.pointer(event, svg.node());
            icon.attr("x", cx + offsetX).attr("y", cy - iconSize / 2);
          }
        })
        .on("mouseout", function (event, d) {
          d3.select(this).classed("hovered-text", false);

          smallCircles.filter((dc) => dc === d).classed("hovered-circle", false);
          link.filter((lt) => lt === d).classed("hovered-link", false);

          if (editIconGroup) {
            editIconGroup.selectAll(".edit-icon").remove();
          }
        })
        .on("click", function (event, d) {
          event.stopPropagation();
          onChangeFunc({
            type: "edit",
            editSource: d.source.title,
            editIndex: d.target.id,
            d: d
          });
        });
    }

    // ** NEW: Hover on child nodes to show / hide gray hover circle **
    node
      .filter((d) => d.id !== 0) // only for child nodes
      .on("mouseover", function (event, d) {
        // place the gray hover circle at the same position as this node
        const [cx, cy] = [d.x, d.y];
        hoverCircle
          .attr("cx", cx)
          .attr("cy", cy)
          .raise() // bring to top
          .transition()
          .duration(100)
          .attr("opacity", 1);
      })
      .on("mouseout", function (event, d) {
        hoverCircle
          .transition()
          .duration(100)
          .attr("opacity", 0);
      });

    function repelSmallFromBig() {
      const big = nodes;
      links.forEach((ld) => {
        const cx = ld._sx;
        const cy = ld._sy;
        if (cx == null || cy == null) return;
        big.forEach((bn) => {
          const bx = bn.x;
          const by = bn.y;
          const br = bn.size != null ? bn.size : 0;
          const minDist =
            br +
            localSettings.smallCircleRadius +
            localSettings.smallCirclePadding;
          const dx = cx - bx;
          const dy = cy - by;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < minDist && dist > 0) {
            const overlap = minDist - dist;
            const push = overlap * localSettings.pushFactor;
            const ux = dx / dist;
            const uy = dy / dist;
            ld._sx += ux * push;
            ld._sy += uy * push;
          }
        });
      });
    }

    simulation.on("tick", () => {
      node
        .attr("cx", (d) => {
          const r = (d.size || 0) + localSettings.nodeMargin;
          d.x = Math.max(r, Math.min(width - r, d.x));
          return d.x;
        })
        .attr("cy", (d) => {
          const r = (d.size || 0) + localSettings.nodeMargin;
          d.y = Math.max(r, Math.min(height - r, d.y));
          return d.y;
        });

      blackDots.attr("cx", (d) => d.x).attr("cy", (d) => d.y);

      const midX = width / 2;
      node.each(function (d) {
        const sel = d3.select(this);
        if (d.x < midX) {
          sel.classed("left", true).classed("right", false);
        } else {
          sel.classed("right", true).classed("left", false);
        }
      });

      labels
        .attr("transform", (d) => {
          const off = (d.size || 0) + 6;
          const lx = d.x < midX ? d.x - off : d.x + off;
          const ly = d.y;
          return `translate(${lx},${ly})`;
        })
        .attr("text-anchor", (d) => (d.x < midX ? "end" : "start"));

      link.attr("d", (d) => {
        const sx = d.source.x;
        const sy = d.source.y;
        const tx = d.target.x;
        const ty = d.target.y;
        const dx = tx - sx;
        const dy = ty - sy;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist === 0) return "";
        const nx = -dy / dist;
        const ny = dx / dist;
        const baseOffset = localSettings.curveOffset;
        const jitter = (Math.random() - 0.5) * localSettings.curveJitter;
        const offset = baseOffset + jitter;
        const cx1 = sx + nx * offset;
        const cy1 = sy + ny * offset;
        const cx2 = tx + nx * offset;
        const cy2 = ty + ny * offset;
        return `M${sx},${sy} C ${cx1},${cy1} ${cx2},${cy2} ${tx},${ty}`;
      });

      smallCircles.attr("transform", (d, i) => {
        const pathEl = link.nodes()[i];
        if (!pathEl) return null;
        try {
          const totalLen = pathEl.getTotalLength();
          const pt = pathEl.getPointAtLength(totalLen * 0.7);
          d._sx = pt.x;
          d._sy = pt.y;
        } catch (err) {
          // fallback
        }
        if (d._sx != null && d._sy != null) {
          const r = localSettings.smallCircleRadius + localSettings.nodeMargin;
          const x = Math.max(r, Math.min(width - r, d._sx));
          const y = Math.max(r, Math.min(height - r, d._sy));
          d._sx = x;
          d._sy = y;
          return `translate(${x},${y})`;
        }
        return null;
      });

      smallTexts.attr("transform", (d) => {
        if (d._sx != null && d._sy != null) {
          const r = localSettings.smallCircleRadius + localSettings.nodeMargin;
          const x = Math.max(r, Math.min(width - r, d._sx));
          const y = Math.max(r, Math.min(height - r, d._sy));
          return `translate(${x},${y})`;
        }
        return null;
      });

      repelSmallFromBig();

      if (isEditable && editIconGroup) {
        editIconGroup.selectAll(".edit-icon").each(function () {
          const icon = d3.select(this);
          const bound = icon.datum();
          if (bound && bound._sx != null && bound._sy != null) {
            const offsetX = localSettings.smallCircleRadius + 4;
            const iconSize = +icon.attr("width");
            icon
              .attr("x", bound._sx + offsetX)
              .attr("y", bound._sy - iconSize / 2);
          }
        });
      }
    });

    function handleNodeClick(d) {
      const totalFadeTime = localSettings.exitDuration;
      const steps = 20;
      const interval = totalFadeTime / steps;
      svg.selectAll("*").interrupt();

      const selCircles = svg.selectAll("circle");
      const selLinks = svg.selectAll("path");
      const selLabels = svg.selectAll("text.label");
      const selSmallCircles = svg.selectAll("circle.smallCircle");
      const selSmallTexts = svg.selectAll("text.smallCircleText");
      const selBlackDots = svg.selectAll("circle.blackDot");
      const selections = [
        selCircles,
        selLinks,
        selLabels,
        selSmallCircles,
        selSmallTexts,
        selBlackDots,
      ];

      function setOpacityOnSel(sel, op) {
        sel.each(function () {
          d3.select(this).style("opacity", op);
        });
      }

      let currentStep = 0;
      const fadeTimer = setInterval(() => {
        currentStep++;
        const newOp = 1 - currentStep / steps;
        selections.forEach((sel) => setOpacityOnSel(sel, Math.max(newOp, 0)));
        if (currentStep >= steps) {
          clearInterval(fadeTimer);
          onClicked({ ...d, source: d.title });
        }
      }, interval);
    }
  };

  useEffect(() => {
    generateGraph();
    return () => {
      d3.select(svgRef.current).selectAll("*").interrupt();
    };
  }, [itemClickedObject, JSON.stringify(localSettings)]);

  return (
    <svg ref={svgRef} width="100%" height={localSettings.height + "px"}></svg>
  );
};

export default ForceGraphDetail;
